// Gemeinsame Bausteine der Preis-Verträge. Geld in Cent je Preiseinheit des Gebindes (`pricePerQty`, 1000 = je
// 1 Gebinde), Mengen in Tausendstel des Gebindes (1 Kasten = 1000), Prozent in Basispunkten (250 = 2,5 %).
// Ob ein Betrag brutto oder netto ist, sagt die Preisliste (`priceMode`) – nie der einzelne Preis.
import { zId } from '@shared/ids'
import { z } from 'zod'

export const PRICE_MODES = ['gross', 'net'] as const
export const zPriceMode = z.enum(PRICE_MODES, { error: 'Bitte brutto oder netto wählen.' })

/** Kalendertag 'YYYY-MM-DD' (Europe/Berlin). */
export const zDay = z.iso.date({ error: 'Bitte ein gültiges Datum eingeben.' })

/** Pflichttext: beschnitten, nicht leer. */
export const zText = (max: number) => z.string({ error: 'Bitte ausfüllen.' }).trim().min(1, { error: 'Bitte ausfüllen.' }).max(max)

/** Freiwilliger Text: '' gilt als „nicht angegeben“ (null); undefined = unverändert. */
export const zOptionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) => (value === '' ? null : value))

/** Preis in Cent, nicht negativ. */
export const zPriceCents = z
  .int({ error: 'Bitte einen Preis eingeben.' })
  .min(0, { error: 'Der Preis darf nicht negativ sein.' })
  .max(100_000_000, { error: 'Der Preis ist zu hoch.' })

/** Preiseinheit in Tausendstel des Gebindes: 1000 = je 1 Gebinde, 100_000 = je 100. */
export const zPricePerQty = z.int({ error: 'Bitte eine Preiseinheit eingeben.' }).min(1, { error: 'Die Preiseinheit muss größer als 0 sein.' }).max(100_000_000)

/** Staffel: ab dieser Menge im Gebinde in Tausendstel (0 = Grundpreis der Liste). */
export const zMinQty = z.int({ error: 'Bitte eine Menge eingeben.' }).min(0, { error: 'Die Staffelmenge darf nicht negativ sein.' }).max(1_000_000_000)

/** Menge im Gebinde in Tausendstel, größer als null. */
export const zUnitQty = z.int({ error: 'Bitte eine Menge eingeben.' }).min(1, { error: 'Die Menge muss größer als 0 sein.' }).max(1_000_000_000)

/** Prozentsatz in Basispunkten (1900 = 19 %). */
export const zBasisPoints = z
  .int({ error: 'Bitte einen Prozentsatz eingeben.' })
  .min(0, { error: 'Der Prozentsatz darf nicht negativ sein.' })
  .max(10_000, { error: 'Mehr als 100 % sind nicht möglich.' })

export const zReason = z
  .string({ error: 'Bitte geben Sie einen Grund an.' })
  .trim()
  .min(3, { error: 'Bitte geben Sie einen Grund an (mindestens 3 Zeichen).' })
  .max(500)

export const zSaved = z.object({ id: zId, version: z.int() })
export const zById = z.strictObject({ id: zId })
export const zExpectedVersion = z.int().positive().optional()
export const zPerson = z.object({ id: zId, name: z.string() })
export const zLocationRef = z.object({ id: zId, code: z.string(), name: z.string() })

/** Gebinde eines Artikels in Preiszeilen. */
export const zUnitRef = z.object({
  /** item_unit.id */
  id: zId,
  code: z.string(),
  /** Kurzform: „Ka“ */
  short: z.string(),
  /** „Kasten 20 × 0,5 l“ */
  label: z.string(),
  /** Basismenge je Gebinde in Tausendstel */
  baseQty: z.int(),
})

/** Spanne einer Preiszeile gegenüber dem aktuellen Einkaufspreis – nur mit „Einstandspreise sehen“. */
export const zMarginInfo = z.object({
  /** Einkaufspreis netto je Gebinde in Cent */
  purchaseCents: z.int(),
  /** Rohertrag netto je Gebinde in Cent */
  marginCents: z.int(),
  /** Handelsspanne in Basispunkten vom Netto-Erlös; null bei Erlös 0 */
  marginBp: z.int().nullable(),
})
export type MarginInfo = z.output<typeof zMarginInfo>

/** Rundungsregeln der Massenänderung: keine · auf …,x9 · auf …,x0 (volle 10 Cent) · auf …,x5 · auf …,99. */
export const ROUNDING_RULES = ['none', 'x9', 'x0', 'x5', 'x99'] as const
export type RoundingRule = (typeof ROUNDING_RULES)[number]
